import {Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {AlarmDeviceModalComponent} from './alarm-device-modal/alarm-device-modal.component';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {faCalendar, faBan, faTimes} from '@fortawesome/free-solid-svg-icons';
import {
    AlarmClassification, AlarmDeviceDto,
    AlarmDeviceSearchCriteriaDto,
    AlarmDevicesService,
    ISortCriteriaDto,
    MeasuringPointType,
    PageableRequestDtoOfAlarmDeviceSearchCriteriaDto,
    PagedResultDtoOfAlarmDeviceDto,
    RegisterType,
    SortCriteriaDto,
    SortDirection
} from '../../_services/configuration-services';
import {Subscription} from 'rxjs';
import {LangChangeEvent, TranslateService} from '@ngx-translate/core';
import {PermissionKeys} from '../../app.constants';
import {SearchIotRegisterComponent} from '../../_shared/_components/search-iot-register/search-iot-register.component';
import {SearchMeasuringPointComponent} from '../../_shared/_components/search-measuring-point/search-measuring-point.component';
import {faPen, faToggleOff, faToggleOn, faTrash} from '@fortawesome/free-solid-svg-icons';
import {NotificationsService} from '../../_shared/notifications.service';
import {BackofficePermissionsService} from '../../_shared/backoffice-permissions.service';
import {FormUtils} from '../../_shared/form-utils';
import {ConfirmModalService} from '../../_shared/_components/confirm-modal/confirm-modal.component';
import {TranslateUtils} from '../../_shared/translate-utils';

@Component({
    selector: 'app-alarm-device-management',
    templateUrl: './alarm-device-management.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AlarmDeviceManagementComponent implements OnInit, OnDestroy {

    @ViewChild('iotRegisterSearch') iotRegisterSearch: SearchIotRegisterComponent;
    @ViewChild('measuringPointSearch') measuringPointSearch: SearchMeasuringPointComponent;
    @ViewChild('devicesTable') devicesTable;

    PermissionKeys = PermissionKeys;
    RegisterType = RegisterType;
    MeasuringPointType = MeasuringPointType;

    // Filters
    filter: {
        formGroup: UntypedFormGroup,
        classificationsDropdownList: string[]
        statesDropdownList: any[]
    } = {
        formGroup: null,
        classificationsDropdownList: Object.keys(AlarmClassification).filter(c => c !== AlarmClassification.Unknown),
        statesDropdownList: [{
            key: 'active',
            value: true
        }, {
            key: 'inactive',
            value: false
        }]
    };

    textInputPattern = FormUtils.textInputPattern;

    // Icons
    icons = {
        calendar: faCalendar,
        edit: faPen,
        delete: faTrash,
        disable: faBan,
        times: faTimes,
        toggleOn: faToggleOn,
        toggleOff: faToggleOff
    };

    // Pagination
    readonly pageSize = 10;
    crtPage = 0;

    // Table content
    devices: PagedResultDtoOfAlarmDeviceDto = new PagedResultDtoOfAlarmDeviceDto();

    private currentSort = new SortCriteriaDto({
        direction: SortDirection.Asc,
        property: 'classification'
    });

    private pageRequest: PageableRequestDtoOfAlarmDeviceSearchCriteriaDto;

    private langChangeSubscription: Subscription;
    crtLang = TranslateUtils.defaultLanguage;

    constructor(private readonly modalService: NgbModal,
                private readonly backofficePermissionsService: BackofficePermissionsService,
                private readonly translateService: TranslateService,
                private readonly notificationsService: NotificationsService,
                private readonly confirmService: ConfirmModalService,
                private readonly alarmDeviceService: AlarmDevicesService) {

        this.filter.formGroup = new UntypedFormGroup({
                classification: new UntypedFormControl(''),
                iotRegister: new UntypedFormControl(''),
                measuringPoint: new UntypedFormControl(''),
                location: new UntypedFormControl(''),
                state: new UntypedFormControl('')
            },
            {updateOn: 'change'});

        this.filter.formGroup.reset();
    }

    canConfigureDevices(): boolean {
        return this.backofficePermissionsService.hasPermission(PermissionKeys.configurationAlertesAlarmes);
    }

    ngOnInit(): void {
        this.crtLang = this.translateService.currentLang;
        this.langChangeSubscription = this.translateService.onLangChange.subscribe(async (event: LangChangeEvent) => {
            this.crtLang = event.lang;
        });

        this.initializeBasePageRequest();

        this.fetchDevices(this.pageRequest);
    }

    ngOnDestroy(): void {
        this.langChangeSubscription.unsubscribe();
        this.modalService.dismissAll('destroy');
    }

    fetchDevices(pageableRequest: PageableRequestDtoOfAlarmDeviceSearchCriteriaDto): void {
        this.alarmDeviceService.searchAlarmDevices(pageableRequest).pipe().subscribe(devices => {
            this.devices = devices;
        });
    }

    activateDevice(deviceId: string): void {
        this.alarmDeviceService.activateAlarmDevice(deviceId).pipe().subscribe(_ => {
            this.notificationsService.success({title: 'alarms.device.notification.activateDeviceSuccess'});
            this.fetchDevices(this.pageRequest);
        });
    }

    deactivateDevice(deviceId: string): void {
        this.alarmDeviceService.deactivateAlarmDevice(deviceId).pipe().subscribe(_ => {
            this.notificationsService.success({title: 'alarms.device.notification.deactivateDeviceSuccess'});
            this.fetchDevices(this.pageRequest);
        });
    }

    createNewDevice(): void {
        this.editDevice(null);
    }

    editDevice(device: AlarmDeviceDto): void {
        const modal = this.modalService.open(AlarmDeviceModalComponent, {centered: true, size: 'lg'});
        modal.componentInstance.alarmDeviceId = device?.id;
        modal.result
            .then(_ => {
                this.fetchDevices(this.pageRequest);
            }, () => { /* catch the rejection */ });
    }

    deleteDevice(device: AlarmDeviceDto): void {
        this.confirmService.confirm({
            titleKey: 'common.confirmModal.title.delete',
            messageKey: 'common.confirmModal.message.deleteAlarmDevice'
        }).then(result => {
            if (result === ConfirmModalService.yes) {
                this.alarmDeviceService.deleteAlarmDevice(device.id).pipe().subscribe(_ => {
                    this.notificationsService.success({title: 'alarms.device.notification.deleteDeviceSuccess'});
                    this.fetchDevices(this.pageRequest);
                });
            }
        });
    }

    applyFilters(): void {
        if (this.filter.formGroup.invalid) {
            return;
        }

        this.crtPage = 0;

        this.initializeBasePageRequest();

        this.pageRequest.criteria = new AlarmDeviceSearchCriteriaDto({
            classification: AlarmClassification[this.classification.value],
            iotRegisterId: this.iotRegister.value?.id,
            measuringPointId: this.measuringPoint.value?.id,
            location: this.location.value,
            active: this.state.value,
            sortCriteriaDto: this.currentSort
        });

        this.fetchDevices(this.pageRequest);
    }

    setPage(page: any): void {
        this.crtPage = page.offset;
        this.pageRequest.page = this.crtPage + 1;

        this.fetchDevices(this.pageRequest);
    }

    onSort(sortOrder: any): void {
        this.crtPage = 0;
        this.pageRequest.page = this.crtPage + 1;

        const sortCriteriaDto: ISortCriteriaDto =
            {
                direction: sortOrder.sorts[0].dir,
                property: sortOrder.sorts[0].prop
            };
        this.pageRequest.criteria.sortCriteriaDto = new SortCriteriaDto(sortCriteriaDto);
        this.currentSort = new SortCriteriaDto(sortCriteriaDto);

        this.fetchDevices(this.pageRequest);
    }

    clearFilter(): void {
        // Clear inputs
        this.filter.formGroup.reset();
        this.iotRegisterSearch.clearInput();
        this.measuringPointSearch.clearInput();

        // clear search
        this.initializeBasePageRequest();
        this.fetchDevices(this.pageRequest);
    }

    get classification(): UntypedFormControl{
        return this.filter.formGroup.get('classification') as UntypedFormControl;
    }

    get iotRegister(): UntypedFormControl{
        return this.filter.formGroup.get('iotRegister') as UntypedFormControl;
    }

    get measuringPoint(): UntypedFormControl{
        return this.filter.formGroup.get('measuringPoint') as UntypedFormControl;
    }

    get location(): UntypedFormControl{
        return this.filter.formGroup.get('location') as UntypedFormControl;
    }

    get state(): UntypedFormControl{
        return this.filter.formGroup.get('state') as UntypedFormControl;
    }

    private initializeBasePageRequest(): void {
        this.pageRequest = new PageableRequestDtoOfAlarmDeviceSearchCriteriaDto({
            page: this.crtPage + 1,
            pageSize: this.pageSize
        });
        this.pageRequest.criteria = new AlarmDeviceSearchCriteriaDto({
            classification: null,
            iotRegisterId: null,
            measuringPointId: null,
            location: null,
            active: null,
            sortCriteriaDto: this.currentSort
        });
    }
}
